import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import {
	Container,
	Stack,
	AppBar,
	Toolbar,
	Typography,
	Dialog,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody
} from '@mui/material';
import { useParams } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import moment from 'moment';
import { DataContext } from '../../../../context/DataContext';
import { getToken, getUser } from '../../../../api/Account';
import { isEmpty } from 'lodash';
import NoData from '../../../../components/NoData';
import { printNominaAsync } from '../../../../api/Employees';
import { simpleMessage } from '../../../../helpers/Helpers';
import { PrintReport } from '../../../../components/PrintReport';

// Constantes para formateo
const currencyFormat = new Intl.NumberFormat('es-NI', {
	style: 'currency',
	currency: 'NIO'
});

// Componente para celda de moneda
const CurrencyCell = ({ value, isPrint = false, isFooter = false }) => (
	<TableCell
		align="center"
		style={isFooter ? { fontSize: 10, fontWeight: 'bold', color: '#2979ff' } : { fontSize: 8 }}
		className={isPrint ? 'text-dark' : ''}
	>
		{currencyFormat.format(value || 0)}
	</TableCell>
);

// Componente para celda de encabezado
const HeaderCell = ({ children, align = 'left', style = {} }) => (
	<TableCell align={align} className="text-primary fw-bold" style={{ fontSize: 9, ...style }}>
		{children}
	</TableCell>
);

// Componente para fila de totales
const TotalsRow = ({ totals }) => {
	if (!totals) return null;

	return (
		<TableRow sx={{ fontWeight: 'bold' }}>
			<TableCell
				align="left"
				style={{ fontSize: 10, fontWeight: 'bold', color: '#2979ff' }}
				colSpan={3}
			>
				TOTALES
			</TableCell>
			<CurrencyCell value={totals.salario} isFooter={true} />
			<CurrencyCell value={totals.montoDiasFeriado} isFooter={true} />
			<CurrencyCell value={totals.montoHorasExtra} isFooter={true} />
			<CurrencyCell value={totals.montoViaticoAlimentacion} isFooter={true} />
			<CurrencyCell value={totals.montoViaticoTransporte} isFooter={true} />
			<CurrencyCell value={totals.totalIngresos} isFooter={true} />
			<CurrencyCell value={totals.aporteINSS} isFooter={true} />
			<CurrencyCell value={totals.aporteIR} isFooter={true} />
			<CurrencyCell value={totals.adelanto} isFooter={true} />
			<CurrencyCell value={totals.otrasDeducciones} isFooter={true} />
			<CurrencyCell value={totals.totalDeducciones} isFooter={true} />
			<CurrencyCell value={totals.inssPatronal} isFooter={true} />
			<CurrencyCell value={totals.inatec} isFooter={true} />
			<CurrencyCell value={totals.totalPercibir} isFooter={true} />
			<TableCell />
		</TableRow>
	);
};

// Componente para el contenido de la tabla
const NominaTable = ({ detail, totals, isPrint = false }) => {
	return (
		<TableContainer>
			<Table aria-label="simple table" size="small">
				<TableHead>
					<TableRow>
						<TableCell colSpan={3} />
						<TableCell
							align="center"
							className="fw-bold bg-success"
							colSpan={4}
							style={{ fontSize: 10 }}
						>
							INGRESOS
						</TableCell>
						<TableCell
							align="center"
							className="fw-bold bg-warning"
							colSpan={5}
							style={{ fontSize: 10 }}
						>
							DEDUCCIONES
						</TableCell>
						<TableCell colSpan={5} />
					</TableRow>
					<TableRow>
						<HeaderCell>#</HeaderCell>
						<HeaderCell>Nombre del Trabajador</HeaderCell>
						<HeaderCell>Cargo</HeaderCell>
						<HeaderCell align="center">S.Quincenal</HeaderCell>
						<HeaderCell align="center">D.Feriado</HeaderCell>
						<HeaderCell align="center">Monto H/E</HeaderCell>
						<HeaderCell align="center">Monto V/A</HeaderCell>
						<HeaderCell align="center">Monto V/T</HeaderCell>
						<HeaderCell align="center">T.Ingresos</HeaderCell>
						<HeaderCell align="center">Inss Lab. 7%</HeaderCell>
						<HeaderCell align="center">IR Laboral</HeaderCell>
						<HeaderCell align="center">A.Salario</HeaderCell>
						<HeaderCell align="center">O.Deducciones</HeaderCell>
						<HeaderCell align="center">T.Deducciones</HeaderCell>
						<HeaderCell align="center">Inss Pat. 21.5%</HeaderCell>
						<HeaderCell align="center">INATEC 2%</HeaderCell>
						<HeaderCell align="center">Neto a Recibir</HeaderCell>
						<HeaderCell align="center">Firma</HeaderCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{detail.map((d) => {
						const { id, empleado, salario, puesto, detalle } = d;

						const totalIngresos =
							salario + detalle.montoDiasFeriado + detalle.montoHorasExtra;
						const totalDeducciones =
							detalle.aporteINSS +
							detalle.aporteIR +
							detalle.otrasDeducciones +
							detalle.adelanto;

						return (
							<TableRow key={id}>
								<TableCell
									align="center"
									component="th"
									scope="row"
									style={{ fontSize: 8 }}
									className={isPrint ? 'text-dark' : ''}
								>
									{id}
								</TableCell>
								<TableCell
									align="left"
									style={{ fontSize: 8 }}
									className={isPrint ? 'text-dark' : ''}
								>
									{empleado.fullName}
								</TableCell>
								<TableCell
									align="left"
									style={{ fontSize: 8 }}
									className={isPrint ? 'text-dark' : ''}
								>
									{puesto.description}
								</TableCell>
								<CurrencyCell value={salario} isPrint={isPrint} />
								<CurrencyCell value={detalle.montoDiasFeriado} isPrint={isPrint} />
								<CurrencyCell value={detalle.montoHorasExtra} isPrint={isPrint} />
								<CurrencyCell
									value={detalle.montoViaticoAlimentacion}
									isPrint={isPrint}
								/>
								<CurrencyCell
									value={detalle.montoViaticoTransporte}
									isPrint={isPrint}
								/>
								<CurrencyCell value={totalIngresos} isPrint={isPrint} />
								<CurrencyCell value={detalle.aporteINSS} isPrint={isPrint} />
								<CurrencyCell value={detalle.aporteIR} isPrint={isPrint} />
								<CurrencyCell value={detalle.adelanto} isPrint={isPrint} />
								<CurrencyCell value={detalle.otrasDeducciones} isPrint={isPrint} />
								<CurrencyCell value={totalDeducciones} isPrint={isPrint} />
								<CurrencyCell value={detalle.inssPatronal} isPrint={isPrint} />
								<CurrencyCell value={detalle.inatec} isPrint={isPrint} />
								<CurrencyCell value={detalle.totalPercibir} isPrint={isPrint} />
								<TableCell />
							</TableRow>
						);
					})}
					<TotalsRow totals={totals} isPrint={isPrint} />
				</TableBody>
			</Table>
		</TableContainer>
	);
};

// Componente de información del encabezado
const HeaderInfo = ({ data, user }) => {
	const salary = data?.detallesActivos?.[0]?.empleado?.salario || 0;

	return (
		<Container maxWidth={false} sx={{ textAlign: 'center' }}>
			<Stack direction={'row'} justifyContent="space-between">
				<Stack>
					<Typography sx={{ color: '#2196f3', textAlign: 'left' }} fontSize={11}>
						Salario:
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{currencyFormat.format(salary)}
					</Typography>
				</Stack>
				<Stack>
					<Typography sx={{ color: '#2196f3', textAlign: 'left' }} fontSize={11}>
						Usuario:
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{user}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Fecha y Hora: ${moment().format('DD/MM/YYYY hh:mm A')}`}
					</Typography>
				</Stack>
			</Stack>
		</Container>
	);
};

const PrintNomina = () => {
	const [data, setData] = useState({});
	const [detail, setDetail] = useState([]);
	const compRef = useRef();
	const { id, tipoContrato } = useParams();
	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();

	// Calcular totales
	const totals = useMemo(() => {
		if (!detail.length) return null;

		return detail.reduce(
			(acc, d) => {
				const { salario, detalle } = d;
				return {
					salario: acc.salario + salario,
					montoDiasFeriado: acc.montoDiasFeriado + detalle.montoDiasFeriado,
					montoHorasExtra: acc.montoHorasExtra + detalle.montoHorasExtra,
					montoViaticoAlimentacion:
						acc.montoViaticoAlimentacion + detalle.montoViaticoAlimentacion,
					montoViaticoTransporte:
						acc.montoViaticoTransporte + detalle.montoViaticoTransporte,
					totalIngresos:
						acc.totalIngresos +
						salario +
						detalle.montoDiasFeriado +
						detalle.montoHorasExtra +
						detalle.montoViaticoAlimentacion +
						detalle.montoViaticoTransporte,
					aporteINSS: acc.aporteINSS + detalle.aporteINSS,
					aporteIR: acc.aporteIR + detalle.aporteIR,
					adelanto: acc.adelanto + detalle.adelanto,
					otrasDeducciones: acc.otrasDeducciones + detalle.otrasDeducciones,
					totalDeducciones:
						acc.totalDeducciones +
						detalle.aporteINSS +
						detalle.aporteIR +
						detalle.otrasDeducciones +
						detalle.adelanto,
					inssPatronal: acc.inssPatronal + detalle.inssPatronal,
					inatec: acc.inatec + detalle.inatec,
					totalPercibir: acc.totalPercibir + detalle.totalPercibir
				};
			},
			{
				salario: 0,
				montoDiasFeriado: 0,
				montoHorasExtra: 0,
				montoViaticoAlimentacion: 0,
				montoViaticoTransporte: 0,
				totalIngresos: 0,
				aporteINSS: 0,
				aporteIR: 0,
				adelanto: 0,
				otrasDeducciones: 0,
				totalDeducciones: 0,
				inssPatronal: 0,
				inatec: 0,
				totalPercibir: 0
			}
		);
	}, [detail]);

	const getData = useCallback(async () => {
		setIsLoading(true);
		const datos = { nominaId: id, tipoContrato: tipoContrato };
		const result = await printNominaAsync(token, datos);

		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setData(result.data.result);
		setDetail(result.data.result.detallesActivos || []);
		setIsLoading(false);
	}, [id, tipoContrato, token, setIsLoading, setIsUnautorized]);

	useEffect(() => {
		getData();
	}, [getData]);

	const pageStyle = `
		@media print {
			html, body {
				height: initial !important;
				overflow: initial !important;
				-webkit-print-color-adjust: exact;
			}
		}
		@media all {
			.pagebreak { display: none; }
		}
		@media print {
			.pagebreak { page-break-before: always; }
		}  
		@page { size: landscape; margin: 4mm; }
	`;

	const renderContent = useCallback(
		(isPrint = false) => {
			const fontSize = isPrint ? 8 : 9;
			const periodText = `PERIODO DEL ${moment(data.desde).format('L')} AL ${moment(data.hasta).format('L')}`;

			return (
				<>
					<Typography fontSize={11} textAlign="center">
						{periodText}
					</Typography>
					<HeaderInfo data={data} user={user} />
					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						<hr />
						{isEmpty(data) ? (
							<NoData />
						) : (
							<NominaTable detail={detail} totals={totals} isPrint={isPrint} />
						)}
					</Container>
				</>
			);
		},
		[data, detail, totals, user]
	);

	return (
		<div>
			<Dialog fullScreen open={true}>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
						<Typography
							sx={{ ml: 2, flex: 1, textAlign: 'center' }}
							variant="h5"
							component="div"
						>
							{title}
						</Typography>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
					</Toolbar>
				</AppBar>

				<Stack display="flex" justifyContent="center">
					<Stack
						direction="row"
						justifyContent="space-between"
						paddingLeft={5}
						paddingRight={5}
						marginTop={1}
						marginBottom={1}
					>
						<Stack />
						<Stack>
							<Typography
								sx={{ color: '#2196f3', textAlign: 'center', fontWeight: 'bold' }}
							>
								{slogan}
							</Typography>
							<Typography
								sx={{ color: '#2196f3', textAlign: 'center', fontWeight: 'bold' }}
								variant="h6"
								component="div"
							>
								Nomina de Personal
							</Typography>
							<Typography fontSize={11} textAlign="center">
								{`PERIODO DEL ${moment(data.desde).format('L')} AL ${moment(data.hasta).format('L')}`}
							</Typography>
						</Stack>
						<ReactToPrint
							trigger={() => (
								<IconButton variant="outlined">
									<PrintRoundedIcon
										style={{ fontSize: 40, color: '#2979ff', width: 60 }}
									/>
								</IconButton>
							)}
							content={() => compRef.current}
							pageStyle={pageStyle}
						/>
					</Stack>
					<HeaderInfo data={data} user={user} />
				</Stack>

				<Container maxWidth={false} sx={{ textAlign: 'center' }}>
					<hr />
					{isEmpty(data) ? (
						<NoData />
					) : (
						<NominaTable detail={detail} totals={totals} isPrint={false} />
					)}
				</Container>
			</Dialog>

			<div style={{ display: 'none' }}>
				<PrintReport
					ref={compRef}
					titulo="Nomina de Personal"
					fecha={`Fecha: ${moment().format('L')}`}
				>
					{renderContent(true)}
				</PrintReport>
			</div>
		</div>
	);
};

export default PrintNomina;
